import { api } from 'api';
import { getFullNameReceiver } from 'components/FormEditReceiver/func';
import {
  ADD_TRANSACTION_RECEIVER_NAME,
  BUSINESS_OF_RECEIVER_TYPE,
  FEMALE,
  MALE,
  PAGE_DEFAULT,
  RESET_ADD_TRANSACTION_INFORMATION_STORE,
  RESET_EDIT_RECEIVER_INFORMATION_STORE,
  SIZE_PAGE_LIMIT_DEFAULT,
} from 'constants';
import { createLatestRequestTracker } from 'helpers/latestRequest';
import { checkMaintenance } from 'helpers/maintenance';
import { isArray, isEmpty } from 'lodash';
import { dispatchStore } from 'store';
import {
  SET_ADD_TRANSACTION_INFORMATION,
  SET_CURRENT_RECEIVER,
  SET_EDIT_RECEIVER_INFORMATION,
  SET_REFRESH_RECEIVER_LIST,
  SET_SHOW_LOADING,
} from 'store/action';
import { arrayUtils, objectUtils } from 'utils';

export const convertFilteredKeyFixed = (filtered = {}) => {
  const filteredFixed = {
    countriesSelected: filtered?.countriesSelected,
    favoriteSelected: filtered?.favoriteSelected,
  };

  return objectUtils.deletePropIsFalsy(filteredFixed);
};

export const fetchReceiversCountries = async (
  token,
  setCountries,
  isReceiverOfBusinessType = false,
) => {
  try {
    const path = isReceiverOfBusinessType ? '/business' : '';

    const { data } = await api.getReceiversCountries(token, path);

    if (data) {
      setCountries && setCountries(data);
    }
  } catch (error) {
    checkMaintenance(error);
    console.error(error?.message);
  }
};

export const fetchReceiversFavorite = async ({ token, setReceivers }) => {
  try {
    const { data } = await api.getFavReceivers(token);

    if (data?.content?.length) {
      setReceivers && setReceivers(data?.content);
    }
  } catch (error) {
    checkMaintenance(error);
    console.error(error?.message);
  }
};

export const getBodyPayloadFiltered = (filtered) => {
  return {
    countryCode: filtered?.countriesSelected?.map((country) => country.code),
    favorited: filtered?.favoriteSelected,
  };
};

const receiversRequestTracker = createLatestRequestTracker();

export const invalidatePendingReceiversFetches = () =>
  receiversRequestTracker.invalidate();

export const fetchReceiversFiltered = async ({
  token = null,
  body = null,
  sort = null,
  page = PAGE_DEFAULT,
  size = SIZE_PAGE_LIMIT_DEFAULT,
  receivers = null,
  setReceivers = null,
  setCurrentPage = null,
  setTotalPages = null,
  setTotalElements = null,
  isDelete = false,
  setFetching = null,
  isInitial,
}) => {
  if (token) {
    try {
      const requestTicket = receiversRequestTracker.begin();
      const sortPayload = sort || {};
      let bodyPayload = body || {};

      bodyPayload = objectUtils.deletePropIsFalsy(bodyPayload);

      if (bodyPayload['favorited'] === 'all') {
        delete bodyPayload['favorited'];
      }

      const { data } = await api.searchReceivers(
        token,
        bodyPayload,
        sortPayload,
        page,
        size,
      );

      if (!receiversRequestTracker.isLatest(requestTicket)) {
        if (
          isInitial &&
          !receiversRequestTracker.isInvalidated(requestTicket)
        ) {
          dispatchStore({
            type: SET_REFRESH_RECEIVER_LIST,
            payload: 0,
          });
        }
        return;
      }

      if (data) {
        const { content, totalPages, totalElements } = data;

        if (receivers?.length || (receivers && isDelete)) {
          setReceivers && setReceivers([...receivers, ...content]);
        } else if (!arrayUtils.isArrayEmpty(content)) {
          setReceivers && setReceivers(content);
        } else if (arrayUtils.isArrayEmpty(content)) {
          if (isArray(receivers)) {
            setReceivers([]);
          } else {
            setReceivers(null);
          }
        }

        setCurrentPage && setCurrentPage(page);

        setTotalPages && setTotalPages(totalPages);

        setTotalElements && setTotalElements(totalElements);

        setFetching && setFetching(false);

        if (isInitial) {
          dispatchStore({
            type: SET_REFRESH_RECEIVER_LIST,
            payload: 0,
          });
        }
      }
    } catch (error) {
      checkMaintenance(error);
      console.error(error?.message);
    }
  }
};

export const fetchReceiverInformation = async (token, receiverId, data) => {
  try {
    let {
      firstname,
      lastname,
      gender,
      mobile,
      phone,
      address,
      banks,
      ewallets,
      favorited,
      relationship,
    } = data || {};

    let isReceiverOfBusinessType = gender === BUSINESS_OF_RECEIVER_TYPE;
    let isReceiverOfIndividualType = gender === MALE || gender === FEMALE;

    let newInformation = {
      receiverId,
      firstName: firstname,
      lastName: lastname,
      fullName: getFullNameReceiver({
        isReceiverOfBusinessType,
        isReceiverOfIndividualType,
        isReceiverTypeUnidentified: isEmpty(gender),
        lastName: lastname,
        firstName: firstname,
        countryCode: address?.country?.code,
      }),
      country: address?.country,
      countryName: address?.country?.name,
      gender,
      address,
      fullAddress: address?.fullAddress,
      mobile,
      mobileNumber: mobile?.number,
      phone,
      phoneNumber: phone?.number,
      banks,
      ewallets,
      favorited,
      relationship,
    };

    const handleSaveToStore = (information) => {
      dispatchStore({ type: SET_CURRENT_RECEIVER, payload: information });
      dispatchStore({
        type: SET_ADD_TRANSACTION_INFORMATION,
        payload: {
          ...RESET_ADD_TRANSACTION_INFORMATION_STORE,
          receiverPayoutMethod: {
            ...RESET_ADD_TRANSACTION_INFORMATION_STORE.receiverPayoutMethod,
            [ADD_TRANSACTION_RECEIVER_NAME]: information,
          },
        },
      });
      dispatchStore({
        type: SET_EDIT_RECEIVER_INFORMATION,
        payload: {
          ...RESET_EDIT_RECEIVER_INFORMATION_STORE,
          residentialAddress: {
            ...RESET_EDIT_RECEIVER_INFORMATION_STORE.residentialAddress,
            isEditReceiverEnterAddressManuallySelected: address?.manual,
          },
        },
      });
    };

    if (!isEmpty(data)) {
      console.log(
        `⭐ fetchReceiverInformation 👉 handleSave 1 ➡️ newInformation ↩`,
        newInformation,
      );
      handleSaveToStore(newInformation);
    }

    const { data: dataReceiver } = await api.getReceiver(token, receiverId);
    if (!isEmpty(dataReceiver)) {
      let {
        firstname,
        lastname,
        gender: genderLatest,
        mobile,
        phone,
        address,
        banks,
        ewallets,
        favorited,
        relationship,
      } = dataReceiver;

      isReceiverOfBusinessType = genderLatest === BUSINESS_OF_RECEIVER_TYPE;
      isReceiverOfIndividualType =
        genderLatest === MALE || genderLatest === FEMALE;

      newInformation = {
        receiverId,
        firstName: firstname,
        lastName: lastname,
        fullName: getFullNameReceiver({
          isReceiverOfBusinessType,
          isReceiverOfIndividualType,
          isReceiverTypeUnidentified: isEmpty(genderLatest),
          lastName: lastname,
          firstName: firstname,
          countryCode: address?.country?.code,
        }),
        country: address?.country,
        countryName: address?.country?.name,
        gender: genderLatest,
        address,
        fullAddress: address?.fullAddress,
        mobile,
        mobileNumber: mobile?.number,
        phone,
        phoneNumber: phone?.number,
        banks,
        ewallets,
        favorited,
        relationship,
      };

      console.log(
        `⭐ fetchReceiverInformation 👉 handleSave 2 ➡️ newInformation ↩`,
        newInformation,
      );
      handleSaveToStore(newInformation);
    }

    return true;
  } catch (error) {
    checkMaintenance(error);
    console.error(
      `🔥 fetchReceiverInformation ❌ handleSave ➡️ error 2 ↩`,
      error?.message,
    );

    return false;
  }
};

export const fetchAllReceivers = async ({ token, setReceivers }) => {
  if (token) {
    try {
      const { data } = await api.getAllReceivers(token);

      if (data) {
        setReceivers(data);
      }

      dispatchStore({
        type: SET_SHOW_LOADING,
        payload: false,
      });
    } catch (error) {
      checkMaintenance(error);
      console.error(error?.message);
      dispatchStore({
        type: SET_SHOW_LOADING,
        payload: false,
      });
    }
  }
};

export const addNewBankAccount = async (token, receiverId, bank) => {
  delete bank['id'];

  bank = {
    ...bank,
    accountName: bank?.accountName?.toUpperCase(),
  };

  return api.addBank(token, receiverId, bank);
};
export const updateBankAccount = async (token, receiverId, bank) => {
  return api.updateBank(token, receiverId, bank.id, bank);
};
export const deleteBankAccount = async (token, receiverId, bankId) => {
  return api.deleteBank(token, receiverId, bankId);
};

export const addNewEwalletAccount = async (token, receiverId, ewallet) => {
  delete ewallet['id'];

  return api.addEwallet(token, receiverId, ewallet);
};
export const updateEwalletAccount = async (token, receiverId, ewallet) => {
  return api.updateEwallet(token, receiverId, ewallet.id, ewallet);
};
export const deleteEwalletAccount = async (token, receiverId, ewalletId) => {
  return api.deleteEwallet(token, receiverId, ewalletId);
};
